import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CAlert,
  CButton,
  CCol,
  CInput,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CRow,
  CSelect,
} from "@coreui/react";
import { Spinner } from "react-bootstrap";
import { getItem } from "../../api/generics";
import {
  OperationalList,
  OperationalLot,
} from "../../models/operational-monitoring";
import { SUCCESS } from "../../utils/constants/tags";

interface Props {
  show: boolean;
  onClose: () => void;
}

const statusLabels: Record<string, string> = {
  uploaded_to_set: "Esperando procesamiento",
  not_uploaded_to_set: "No subido",
  error: "Error",
  unknown_error: "Error desconocido",
  unrecoverable_error: "Error no recuperable",
};

const LOTS_PAGE_SIZE = 50;

const normalize = (value: unknown) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const OperationalLotsModal: React.FC<Props> = ({ show, onClose }) => {
  const [lots, setLots] = useState<OperationalLot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [responseCode, setResponseCode] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const loadLots = useCallback(async (requestedPage: number, requestedStatus = "") => {
    setLoading(true);
    setError(null);
    const response = await getItem<OperationalList<OperationalLot>>(
      "/admin-stats/operations/lots/",
      new Map([
        ["limit", String(LOTS_PAGE_SIZE)],
        ["offset", String(requestedPage * LOTS_PAGE_SIZE)],
        ["status", requestedStatus],
      ]),
    );
    if (response.status === SUCCESS && response.data) {
      setLots(response.data.items);
      setHasMore(response.data.hasMore ?? false);
    } else {
      setError(response.detail ?? "No se pudieron cargar los lotes.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (show) {
      setPage(0);
      loadLots(0);
    }
  }, [show, loadLots]);

  const goToPage = async (nextPage: number) => {
    setPage(nextPage);
    await loadLots(nextPage, status);
  };

  const changeStatus = async (nextStatus: string) => {
    setStatus(nextStatus);
    setPage(0);
    await loadLots(0, nextStatus);
  };

  const filteredLots = useMemo(() => {
    const needle = normalize(search);
    const errorCode = normalize(responseCode);
    return lots.filter((lot) => {
      if (status && lot.status !== status) return false;
      if (errorCode && !normalize(lot.responseCode).includes(errorCode)) return false;
      if (!needle) return true;
      return normalize(
        [
          lot.id,
          lot.lotNumber,
          lot.taxPayerRuc,
          lot.taxPayerName,
          lot.responseCode,
          lot.responseDescription,
          lot.error,
        ].join(" "),
      ).includes(needle);
    });
  }, [lots, responseCode, search, status]);

  return (
    <CModal show={show} onClose={onClose} size="xl">
      <CModalHeader closeButton>
        <CModalTitle>Lotes operativos de SIFEN</CModalTitle>
      </CModalHeader>
      <CModalBody style={{ maxHeight: "82vh", overflowY: "auto" }}>
        {error && <CAlert color="danger">{error}</CAlert>}
        <CRow className="mb-3">
          <CCol md="5">
            <CInput
              value={search}
              onChange={(event: any) => setSearch(event.target.value)}
              placeholder="Lote, contribuyente o descripción"
              aria-label="Buscar lotes"
            />
          </CCol>
          <CCol md="4">
            <CSelect
              value={status}
              onChange={(event: any) => changeStatus(event.target.value)}
            >
              <option value="">Todos los estados operativos</option>
              <option value="uploaded_to_set">Esperando procesamiento</option>
              <option value="not_uploaded_to_set">No subido</option>
              <option value="error">Error</option>
              <option value="unknown_error">Error desconocido</option>
              <option value="unrecoverable_error">Error no recuperable</option>
            </CSelect>
          </CCol>
          <CCol md="3">
            <CInput
              value={responseCode}
              onChange={(event: any) => setResponseCode(event.target.value)}
              placeholder="Código de respuesta"
              aria-label="Filtrar código de respuesta"
            />
          </CCol>
        </CRow>

        <div className="small text-muted mb-2">
          Página {page + 1}: {filteredLots.length} visible(s) de {lots.length} lote(s)
          cargado(s), ordenados desde el más reciente.
        </div>

        <div className="table-responsive" style={{ minHeight: 180 }}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <table className="table table-sm table-hover table-bordered">
              <thead className="thead-light">
                <tr>
                  <th>ID / lote SIFEN</th>
                  <th>Contribuyente</th>
                  <th>Estado</th>
                  <th>Respuesta / error</th>
                  <th>Reintentos</th>
                  <th>Próximo intento</th>
                  <th>Actualizado</th>
                </tr>
              </thead>
              <tbody>
                {filteredLots.map((lot) => (
                  <tr key={lot.id}>
                    <td>
                      <div>#{lot.id}</div>
                      <span className="text-muted">{lot.lotNumber || "Sin número"}</span>
                    </td>
                    <td>
                      <div>{lot.taxPayerName}</div>
                      <span className="text-muted">{lot.taxPayerRuc}</span>
                    </td>
                    <td>{statusLabels[lot.status] ?? lot.status}</td>
                    <td style={{ minWidth: 260 }}>
                      <strong>{lot.responseCode || "-"}</strong>
                      <div>{lot.responseDescription || lot.error || "-"}</div>
                    </td>
                    <td>{lot.retryCount ?? 0}</td>
                    <td>
                      {lot.nextRetryAt ? new Date(lot.nextRetryAt).toLocaleString() : "-"}
                    </td>
                    <td>{new Date(lot.updatedAt).toLocaleString()}</td>
                  </tr>
                ))}
                {filteredLots.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      No hay lotes para los filtros aplicados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div className="d-flex justify-content-center mt-3">
          <CButton
            color="secondary"
            variant="outline"
            className="mr-2"
            onClick={() => goToPage(page - 1)}
            disabled={loading || page === 0}
          >
            Anterior
          </CButton>
          <CButton
            color="secondary"
            variant="outline"
            onClick={() => goToPage(page + 1)}
            disabled={loading || !hasMore}
          >
            Siguiente
          </CButton>
        </div>
      </CModalBody>
    </CModal>
  );
};

export default OperationalLotsModal;
